import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "nativewind/jsx-runtime";
import React from 'react';
import styled from 'styled-components/native';
import ReviewOutlineIcon from '@/assets/icon/ic_review_outline.svg';
import { BadgeShell, BadgeText } from '@/components/BadgeShell';
import PlaceTags from '@/components/PlaceTags';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
const ScoreColorMap = {
    '0': { background: '#E6F4EB', text: '#06903B' },
    '1': { background: '#F0F9E7', text: '#64C40F' },
    '2': { background: '#FFF9E6', text: '#FFC109' },
    '3': { background: '#FFF4E6', text: '#FF9202' },
    '4': { background: '#FFEEE9', text: '#FF5722' },
    '5': { background: '#FCE9E9', text: '#E52123' },
    unknown: { background: '#E7E8E9', text: '#9A9B9F' },
    progress: { background: '#ffffff', text: '#FFC109', border: '#FFC109' },
};
function getScoreStatus(score, isProcessing) {
    if (isProcessing) {
        return 'progress';
    }
    else if (score === undefined || score === null) {
        return 'unknown';
    }
    else if (score <= 0) {
        return '0';
    }
    else if (score <= 1) {
        return '1';
    }
    else if (score <= 2) {
        return '2';
    }
    else if (score <= 3) {
        return '3';
    }
    else if (score <= 4) {
        return '4';
    }
    else {
        return '5';
    }
}
export default function V2SummarySection({ place, accessibilityScore, accessibility, reviewCount, placeListNameChips, onNameLayout, }) {
    var _a, _b;
    const hasScore = accessibilityScore !== undefined && accessibilityScore !== null;
    const isProcessing = !hasScore &&
        !!(accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility) &&
        !(accessibility === null || accessibility === void 0 ? void 0 : accessibility.buildingAccessibility);
    const scoreStatus = getScoreStatus(accessibilityScore, isProcessing);
    const tagTexts = (() => {
        var _a;
        const pa = accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility;
        if (!pa) {
            return [];
        }
        let floorTag;
        let slopeTag;
        const floors = (_a = pa.floors) !== null && _a !== void 0 ? _a : [];
        const floorText = floors.length
            ? floors[0] < 0
                ? `지하 ${-floors[0]}`
                : `${floors[0]}`
            : undefined;
        floorTag = floorText
            ? `${floorText}층${floors.length > 1 ? '+' : ''}`
            : undefined;
        slopeTag = pa.hasSlope
            ? '경사로있음'
            : !pa.hasSlope && (accessibilityScore !== null && accessibilityScore !== void 0 ? accessibilityScore : 0) !== 0
                ? '경사로없음'
                : undefined;
        return [floorTag, slopeTag].filter(tag => tag);
    })();
    const hasReview = reviewCount > 0;
    const tagsRow = (accessibility === null || accessibility === void 0 ? void 0 : accessibility.placeAccessibility) ? (_jsxs(V2TagsRow, { children: [tagTexts.map((text, index) => (_jsxs(React.Fragment, { children: [index > 0 && _jsx(V2TagDot, {}), _jsx(V2TagText, { children: text })] }, index))), hasReview && (_jsxs(_Fragment, { children: [tagTexts.length > 0 && _jsx(V2TagDot, {}), _jsxs(V2ReviewContainer, { children: [_jsx(ReviewOutlineIcon, { width: 16, height: 16, color: color.gray60 }), _jsxs(V2TagText, { children: ['리뷰 ', _jsx(V2ReviewCount, { children: reviewCount })] })] })] }))] })) : null;
    return (_jsxs(Container, { children: [_jsxs(StairLevelRow, { children: [_jsx(BadgeShell, { backgroundColor: ScoreColorMap[scoreStatus].background, textColor: ScoreColorMap[scoreStatus].text, borderColor: (_a = ScoreColorMap[scoreStatus].border) !== null && _a !== void 0 ? _a : ScoreColorMap[scoreStatus].background, children: _jsx(BadgeText, { textColor: ScoreColorMap[scoreStatus].text, style: { letterSpacing: -0.24 }, children: hasScore
                                ? `접근레벨 ${accessibilityScore}`
                                : isProcessing
                                    ? '계산중(건물정보 필요)'
                                    : '접근레벨 -' }) }), ((_b = placeListNameChips === null || placeListNameChips === void 0 ? void 0 : placeListNameChips.length) !== null && _b !== void 0 ? _b : 0) > 0 && (_jsx(PlaceTags, { chips: placeListNameChips !== null && placeListNameChips !== void 0 ? placeListNameChips : [] }))] }), _jsx(NameContainer, { onLayout: onNameLayout, children: _jsx(PlaceName, { children: place.name }) }), tagsRow] }));
}
// --- styled components ---
const Container = styled.View `
  padding-left: 20px;
  padding-top: 4px;
`;
const NameContainer = styled.View `
  margin-top: 8px;
  padding-bottom: 6px;
`;
const PlaceName = styled.Text `
  font-family: ${font.pretendardSemibold};
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  color: ${color.black};
`;
const V2TagsRow = styled.View `
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
`;
const V2TagDot = styled.View `
  width: 2px;
  height: 2px;
  border-radius: 1px;
  background-color: ${color.gray30};
`;
const V2TagText = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.26px;
  color: ${color.gray60};
`;
const V2ReviewContainer = styled.View `
  flex-direction: row;
  align-items: flex-end;
  gap: 2px;
`;
const V2ReviewCount = styled.Text `
  font-family: ${font.pretendardRegular};
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.26px;
  color: ${color.brand50};
`;
// 접근레벨 배지 + 저장리스트 태그를 한 줄로 묶어 가로 스크롤.
const StairLevelRow = styled.ScrollView.attrs({
    horizontal: true,
    showsHorizontalScrollIndicator: false,
    contentContainerStyle: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
}) `
  overflow: hidden;
`;
